import MediaQuery from './Modules/MediaQuery'

class HeroStack {
    /**
     * Disables the normal Glide transition
     *
     * @param  {Glide} Glide
     * @param  {Object} Components
     * @param  {EventsBus} Events
     * @return {Object}
     */
    static DisableTransition(Glide, Components, Events) {
        return {
            modify(translate) {
                return 0
            },
        }
    }

    constructor(heroStackGlide) {
        this.heroStackGlide = heroStackGlide
        this.slides = this.heroStackGlide.querySelector('.glide__slides')
        this.heroElements = this.slides.querySelectorAll('.hero-element')
        this.navigation = this.heroStackGlide.querySelector('.hero-stack-navigation')
        this.navigationIndicator = this.navigation.querySelector('.hero-stack-navigation__indicator')
        this.indicatorAnimationBar = this.navigationIndicator.querySelector('.js-indicator-bar')
        this.navigationButtons = this.navigation.querySelectorAll('.hero-stack-navigation__button')
        this.glideConfiguration = JSON.parse(this.heroStackGlide.dataset.configuration)
        this.isAutoplayEnabled = this.glideConfiguration.default.autoplay !== false
        this.setSlidesToBiggestElement(this.slides, this.heroElements)

        document.addEventListener('afterHubSpotFormLoadedCompletelyEvent', () => {
            // check heights after HubSpot form was loaded
            this.resizeSlides()
        })

        this.heroStackGlide.addEventListener('beforeGlideMount', (e) => {
            const glide = e.detail

            this.glideInstance = e.detail

            // only observe if an autoplay duration is set
            if (this.isAutoplayEnabled) {
                this.addInViewportObserver()
            }

            // Disable Transition
            glide.mutate([HeroStack.DisableTransition])

            glide.on('mount.before', () => {
                // Set height/width of navigation indicator on load
                this.setInitialNavigationSize()

                if (this.isAutoplayEnabled) {
                    this.initNavigationIndicatorAnimation()
                }
            })

            glide.on('mount.after', () => {
                // Adjust glide height on mobile on load
                if (!MediaQuery.UpMd.matches) {
                    this.removeCalculatedSlideHeights()
                    this.setSlidesHeightToActiveSlide()
                }
                this.updateNavigationTheme(this.getHeroTheme())
            })

            // Disable background fade in on initial page load
            glide.on('run.before', () => {
                this.removePreload()
            })

            glide.on('run.after', () => {
                // Set height/width and position of navigation indicator on slide change
                this.setNavigationSizeAndOffset(glide)
                // Adjust glide height on mobile on slide change
                this.setSlidesHeightToActiveSlide()
                this.updateNavigationTheme(this.getHeroTheme())
                if (this.isAutoplayEnabled) {
                    this.restartNavigationIndicatorAnimation()
                }
            })

            glide.on('resize', () => {
                // Change navigation indicator size on resize
                this.setNavigationSizeAndOffset(glide)
                // Adjust glide height on resize
                this.resizeSlides()
                if (this.isAutoplayEnabled) {
                    this.restartNavigationIndicatorAnimation()
                }
            })

            glide.on(['build.before'], function () {
                document.body.classList.add('visible')
            })
        })
    }

    updateNavigationTheme(newTheme) {
        this.removeThemeFromNavigation()
        this.addThemeToNavigation(newTheme)
    }

    addThemeToNavigation(newTheme) {
        if (newTheme) {
            this.navigation.classList.add(newTheme)
        } else {
            this.navigation.classList.add('theme--black-to-blue-gradient')
        }
    }

    removeThemeFromNavigation() {
        this.navigation.classList.forEach((className) => {
            if (className.includes('theme--')) {
                this.navigation.classList.remove(className)
            }
        })
    }

    getHeroTheme() {
        let activeGlideElement = this.heroStackGlide.querySelector('.glide__slide--active')
        let glideWrapper = activeGlideElement.querySelector('.hero-element-wrapper')
        let gradientCircle = activeGlideElement.querySelector('.js-hero-element-gradient-circle')
        let theme = ''

        // special case if gradientCircle is present
        if (gradientCircle && gradientCircle.classList.contains('hero-element__gradient-circle--right')) {
            return 'theme--black-to-blue-gradient'
        }

        glideWrapper.classList.forEach((className) => {
            if (className.includes('theme--')) {
                theme = className
            }
        })
        return theme
    }

    /**
     * @private
     */
    resizeSlides() {
        if (MediaQuery.UpMd.matches) {
            this.setSlidesToBiggestElement()
        } else {
            this.removeCalculatedSlideHeights()
            this.setSlidesHeightToActiveSlide()
        }
    }

    /**
     * @private
     *
     * observe herostack slider if it is in view to start autoplay if set and stop if it is not
     */
    addInViewportObserver() {
        const inViewportObserver = new IntersectionObserver(
            (entries, observer) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        //start autoplay
                        this.glideInstance.play()
                    } else {
                        //stop autoplay
                        this.glideInstance.pause()
                    }
                })
            },
            {
                rootMargin: '0px 0px 0px 0px',
                threshold: 0.5, // half of item height
            }
        )
        inViewportObserver.observe(this.heroStackGlide)
    }

    /**
     * @private
     */
    setSlidesHeightToActiveSlide() {
        const activeElement = this.slides.querySelector('.glide__slide--active > .hero-element-wrapper')

        const computedStyle = getComputedStyle(activeElement)
        const padding = parseFloat(computedStyle.paddingTop + computedStyle.paddingBottom)
        const elementHeight = activeElement.clientHeight - padding

        this.slides.style.height = elementHeight + 'px'
    }

    /**
     * @private
     */
    removeCalculatedSlideHeights() {
        this.heroElements.forEach((element) => {
            element.style.height = null
        })
        this.slides.style.height = null
    }

    /**
     * @private
     */
    setSlidesToBiggestElement() {
        let biggest = null
        this.heroElements.forEach((element) => {
            // reset element height
            element.style.height = null

            let computedStyle = getComputedStyle(element)
            let padding = parseFloat(computedStyle.paddingTop + computedStyle.paddingBottom)
            biggest = Math.max(biggest, element.clientHeight - padding)
        })

        this.slides.style.height = biggest + 'px'
        this.heroElements.forEach((element) => {
            element.style.height = biggest + 'px'
        })
    }

    /**
     * @private
     */
    removePreload() {
        const preloads = document.querySelectorAll('.disable-initial-animation')
        preloads.forEach((preload) => {
            preload.classList.remove('disable-initial-animation')
        })
    }

    /**
     * @private
     */
    setInitialNavigationSize() {
        if (MediaQuery.UpMd.matches) {
            this.navigationIndicator.style.height = this.navigationButtons[0].clientHeight + 'px'
        } else {
            this.navigationIndicator.style.width = 100 / this.heroElements.length + '%'
        }
    }

    initNavigationIndicatorAnimation() {
        this.heroStackGlide.addEventListener('mouseover', () => {
            this.stopNavigationIndicatorAnimation()
        })
        this.heroStackGlide.addEventListener('mouseout', () => {
            this.indicatorAnimationBar.classList.add('animate')
        })
        this.indicatorAnimationBar.classList.add('animate')
    }

    stopNavigationIndicatorAnimation() {
        this.indicatorAnimationBar.classList.remove('animate')
        this.indicatorAnimationBar.offsetWidth
    }

    restartNavigationIndicatorAnimation() {
        this.indicatorAnimationBar.classList.remove('animate')
        this.indicatorAnimationBar.offsetWidth
        this.indicatorAnimationBar.classList.add('animate')
    }

    /**
     * @private
     * @param glide
     */
    setNavigationSizeAndOffset(glide) {
        if (MediaQuery.UpMd.matches) {
            const activeButton = this.heroStackGlide.querySelector('.glide__bullet--active')
            this.navigationIndicator.style.width = null
            this.navigationIndicator.style.height = activeButton.clientHeight + 'px'
            this.navigationIndicator.style.transform = 'translateY(' + activeButton.offsetTop + 'px)'
        } else {
            this.navigationIndicator.style.width = 100 / this.heroElements.length + '%'
            this.navigationIndicator.style.height = null
            // The + 1 is here to compensate for the rounding error introduced by clientWidth
            this.navigationIndicator.style.transform =
                'translateX(' + glide.index * (this.navigationIndicator.clientWidth + 1) + 'px)'
        }
    }
}

function initHeroStacks() {
    const heroStackGlides = document.querySelectorAll('.hero-stack .glide')
    heroStackGlides.forEach((heroStackGlide) => {
        new HeroStack(heroStackGlide)
    })

    if (heroStackGlides.length === 0) {
        document.body.classList.add('visible')
    }
}

if (document.readyState !== 'loading') {
    initHeroStacks()
} else {
    document.addEventListener('DOMContentLoaded', initHeroStacks)
}
